import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { authAPI, subscriptionAPI } from '@/lib/api';
import { Sparkles, Zap, Trophy, BookOpen, Users, Target, Award, MessageSquare, Image, UserPlus, Wand2, Crown, Shield, CreditCard, Smile, Flame, Radio } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userData, subData] = await Promise.all([
        authAPI.getMe(),
        subscriptionAPI.getStatus()
      ]);
      setUser(userData);
      setSubscription(subData);
    } catch (error) {
      console.error('Load error:', error);
    }
  };

  const isAdmin = subscription?.is_admin;

  return (
    <div className="min-h-screen" data-testid="dashboard">
      <nav className="px-6 py-4 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <Sparkles className="h-7 w-7 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">TerryLBPrompts</h1>
          </div>
          {user && (
            <div className="flex items-center gap-4">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/admin')}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <Shield className="h-4 w-4 mr-1" />
                  Admin
                </Button>
              )}
              <a 
                href="mailto:computerwork2022@gmail.com"
                className="text-sm text-muted-foreground hover:text-primary transition-colors hidden md:block"
              >
                Contact Support
              </a>
              <div className="bg-secondary px-4 py-2 rounded-lg border border-border">
                <span className="font-semibold text-sm text-foreground">Level {user.level} • {user.points} pts</span>
              </div>
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-border" />
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-3 text-foreground">Welcome back, <span className="text-primary">{user?.name?.split(' ')[0] || 'there'}</span></h2>
          <p className="text-xl text-muted-foreground">Ready to craft some amazing prompts today?</p>
        </div>

        {/* Credit Status Card */}
        <Card className="p-6 mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-foreground">
                  {isAdmin ? 'Administrator' : `${subscription?.plan_name || 'Free'} Plan`}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isAdmin 
                    ? 'Unlimited access to all features' 
                    : subscription?.subscription_end 
                      ? `Renews ${new Date(subscription.subscription_end).toLocaleDateString()}`
                      : 'Upgrade for more credits'
                  }
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="flex items-center gap-1 text-2xl font-bold text-purple-600">
                  <Image className="h-5 w-5" />
                  {isAdmin ? '∞' : subscription?.image_credits || 0}
                </div>
                <p className="text-xs text-muted-foreground">Images</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1 text-2xl font-bold text-pink-600">
                  <Wand2 className="h-5 w-5" />
                  {isAdmin ? '∞' : subscription?.video_credits || 0}
                </div>
                <p className="text-xs text-muted-foreground">Videos</p>
              </div>
              {!isAdmin && (
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {subscription?.plan === 'free' ? 'Upgrade' : 'Manage'}
                </Button>
              )}
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* DailyDrop AI - Featured New Feature */}
          <Card 
            className="p-6 bg-gradient-to-br from-purple-900 via-violet-800 to-purple-900 border-2 border-amber-400/50 card-shadow-lg cursor-pointer hover:shadow-xl transition-all lg:col-span-2 relative overflow-hidden"
            onClick={() => navigate('/daily-drop')}
            data-testid="daily-drop-card"
          >
            <div className="absolute top-2 right-2">
              <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                NEW
              </span>
            </div>
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                <Flame className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2 text-white">DailyDrop AI</h3>
                <p className="text-purple-200 mb-4 leading-relaxed">
                  Create viral content daily with AI-powered captions, multi-platform scheduling, 
                  streaks, and growth analytics. Your daily creator companion!
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-white/10 text-purple-200 text-xs px-3 py-1 rounded-full">AI Captions</span>
                  <span className="bg-white/10 text-purple-200 text-xs px-3 py-1 rounded-full">Multi-Platform</span>
                  <span className="bg-white/10 text-purple-200 text-xs px-3 py-1 rounded-full">Streaks</span>
                  <span className="bg-white/10 text-purple-200 text-xs px-3 py-1 rounded-full">Analytics</span>
                </div>
                <Button className="bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg shadow-md hover:shadow-lg font-semibold">
                  Start Your Daily Drop
                </Button>
              </div>
            </div>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/builder')}
            data-testid="builder-card"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Prompt Builder</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Create perfect prompts with our smart Q&A wizard</p>
            <Button className="bg-primary text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Start Building
            </Button>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/challenge')}
            data-testid="challenge-card"
          >
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <Target className="h-6 w-6 text-accent" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Daily Challenge</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Practice & improve with AI-powered feedback</p>
            <Button className="bg-accent text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Take Challenge
            </Button>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/library')}
            data-testid="library-card"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Community Library</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Browse winning prompts from the community</p>
            <Button className="bg-primary text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Explore Library
            </Button>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/leaderboard')}
            data-testid="leaderboard-card"
          >
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <Trophy className="h-6 w-6 text-accent" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Leaderboard</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">See top prompt crafters & your ranking</p>
            <Button className="bg-accent text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              View Rankings
            </Button>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/lessons')}
          >
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Mini Lessons</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Quick tutorials on prompt engineering</p>
            <Button className="bg-primary text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Watch Tutorials
            </Button>
          </Card>

          <Card 
            className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/badges')}
          >
            <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
              <Award className="h-6 w-6 text-accent" />
            </div>
            <h3 className="text-xl font-semibold mb-3 text-foreground">Your Badges ({user?.badges?.length || 0})</h3>
            <div className="flex gap-2 flex-wrap mb-4">
              {user?.badges?.slice(0, 3).map((badge, i) => (
                <span key={i} className="bg-accent/10 px-3 py-1 rounded-full text-xs font-semibold border border-accent/20 text-accent">
                  {badge.replace(/_/g, ' ')}
                </span>
              ))}
              {user?.badges?.length > 3 && (
                <span className="bg-accent/10 px-3 py-1 rounded-full text-xs font-semibold border border-accent/20 text-accent">
                  +{user.badges.length - 3} more
                </span>
              )}
            </div>
            <Button variant="outline" className="border border-border rounded-lg w-full font-semibold">
              View All Badges
            </Button>
          </Card>

          <Card 
            className="p-6 bg-white border border-border card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/comments')}
            data-testid="comments-card"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
              <MessageSquare className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Community Feedback</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Share your thoughts and read what others say</p>
            <Button className="bg-primary text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              View Comments
            </Button>
          </Card>

          {/* Photo Composer */}
          <Card 
            className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/photo-composer')}
            data-testid="photo-composer-card"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center mb-4">
              <UserPlus className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Photo Composer</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Combine photos - meet celebrities or reunite with friends!</p>
            <Button className="bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Compose Photos
            </Button>
          </Card>

          <Card 
            className="p-6 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/ai-studio')}
            data-testid="ai-studio-card"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center mb-4">
              <Wand2 className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">AI Studio</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Style transfer, age transform, backgrounds, avatars & more!</p>
            <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Open Studio
            </Button>
          </Card>

          <Card 
            className="p-6 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-300 card-shadow-lg cursor-pointer hover:shadow-xl transition-all"
            onClick={() => navigate('/emoji-studio')}
            data-testid="emoji-studio-card"
          >
            <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center mb-4">
              <Smile className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Emoji Studio</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Build beautiful AI images using just emojis!</p>
            <Button className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Try Emojis
            </Button>
          </Card>

          {/* Live Studio - Multistreaming */}
          <Card 
            className="p-6 bg-gradient-to-br from-red-50 to-purple-50 border border-red-200 card-shadow-lg cursor-pointer hover:shadow-xl transition-all relative overflow-hidden"
            onClick={() => navigate('/live-studio')}
            data-testid="live-studio-card"
          >
            <div className="absolute top-2 right-2">
              <span className="bg-gradient-to-r from-red-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                NEW
              </span>
            </div>
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-purple-500 rounded-lg flex items-center justify-center mb-4">
              <Radio className="h-6 w-6 text-white" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-foreground">Live Studio</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">Stream to YouTube, Twitch, Facebook & more at once!</p>
            <Button className="bg-gradient-to-r from-red-500 to-purple-500 text-white rounded-lg w-full shadow-md hover:shadow-lg font-semibold">
              Go Live
            </Button>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
